/**
 * AetherUserManagement
 * Admin-only UI for managing Aether Dashboard users (admin / partner roles).
 *
 * Features:
 *  - Mehr Details pro User: Last Login, Created, Login-Count, Super-Admin-Krone
 *  - Edit-Modal: Name + E-Mail + Rolle + Active + Passwort
 *  - Kick (force logout): bumpt session_version → alle Tokens invalid
 *  - Send Login-Info: Welcome-Mail mit Login-URL
 *  - Send Password-Reset: Token-basierter Reset-Link
 *  - Super-Admin-Schutz: Andere Admins kann nur admin@davidmedia.de bearbeiten/löschen/kicken
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAetherAuth } from './AetherAuthContext';
import {
  Users, UserPlus, Loader2, Mail, Trash2, Shield, User as UserIcon,
  Search, X, AlertCircle, CheckCircle2, KeyRound, Crown, LogOut,
  Pencil, Send, Clock, ChevronRight, ArrowLeft, Package, Activity, DollarSign,
  Inbox, Key,
} from 'lucide-react';

export default function AetherUserManagement() {
  const { authFetch, user: me } = useAetherAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);          // user object being edited
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Nur noch die aktiven Konten: Account-Anfragen gibt es nicht mehr, seit sich
  // jeder selbst registrieren kann.
  // approving an applicant feels like one continuous workflow.

  // Detail view — when set, the card grid is replaced by the per-user detail page.
  const [detailUserId, setDetailUserId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const openDetail = useCallback(async (user) => {
    setDetailUserId(user.id);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const res = await authFetch(`/api/aether/auth/users/${user.id}/detail`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler beim Laden');
      setDetailData(data);
    } catch (err) {
      setError(err.message);
      setDetailUserId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [authFetch]);

  const closeDetail = useCallback(() => {
    setDetailUserId(null);
    setDetailData(null);
  }, []);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'customer', create_affiliate: true, commission_rate: 20, grant_products: ['instant-curve', 'media-flow', 'instant-grade'] });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch('/api/aether/auth/users');
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler beim Laden');
      setUsers(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); }
    else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 4000);
  };

  // ===== Permission helpers =====
  // The super-admin is the only one who can touch other admins. Everyone can
  // touch partners; everyone can touch their own profile.
  const meIsSuperAdmin = !!me?.is_super_admin;
  const canManage = (target) => {
    if (!target) return false;
    if (target.id === me?.id) return true;            // self
    if (target.role !== 'admin') return true;         // touching a partner: any admin OK
    return meIsSuperAdmin;                            // touching another admin: super only
  };

  // Grant a partner the selected products via the admin grant tool. All three
  // grantable products -> one Suite key ('all'); otherwise one key per product.
  // Returns the list of product ids that failed (empty = all good).
  const grantPartnerProducts = async (email, productIds) => {
    const ALL = ['instant-curve', 'media-flow', 'instant-grade'];
    const toGrant = ALL.every((p) => productIds.includes(p)) ? ['all'] : productIds;
    const failed = [];
    for (const pid of toGrant) {
      try {
        const r = await authFetch('/api/admin/grant-product', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, product_id: pid, license_type: 'single', note: 'Partner-Freischaltung' }),
        });
        if (!r.ok) failed.push(pid);
      } catch { failed.push(pid); }
    }
    return failed;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password || !newUser.name) {
      flash('Bitte alle Felder ausfüllen', true); return;
    }
    if (newUser.password.length < 8) {
      flash('Passwort muss min. 8 Zeichen haben', true); return;
    }
    setCreating(true);
    try {
      const payload = {
        email: newUser.email,
        password: newUser.password,
        name: newUser.name,
        role: newUser.role,
        create_affiliate: newUser.create_affiliate,
      };
      // Provision only matters for partner/admin (they get an affiliate).
      if (newUser.role === 'partner' || newUser.role === 'admin') {
        payload.commission_rate = Math.max(0, Math.min(100, Number(newUser.commission_rate) || 0)) / 100;
      }
      const res = await authFetch('/api/aether/auth/users', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      // Unlock the selected products for a new partner (reuses the grant tool).
      let grantNote = '';
      if (newUser.role === 'partner' && (newUser.grant_products || []).length) {
        const failed = await grantPartnerProducts(data.email, newUser.grant_products);
        grantNote = failed.length ? ` (Freischaltung fehlgeschlagen: ${failed.join(', ')})` : ' + Produkte freigeschaltet';
      }
      flash(`Nutzer "${data.email}" angelegt${grantNote}`);
      setShowCreate(false);
      setNewUser({ email: '', password: '', name: '', role: 'customer', create_affiliate: true, commission_rate: 20, grant_products: ['instant-curve', 'media-flow', 'instant-grade'] });
      loadUsers();
    } catch (err) {
      flash(err.message, true);
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (user) => {
    if (!canManage(user)) { flash('Nur Super-Admin kann andere Admins (de)aktivieren', true); return; }
    try {
      const res = await authFetch(`/api/aether/auth/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !user.is_active }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      flash(user.is_active ? `"${user.email}" deaktiviert` : `"${user.email}" aktiviert`);
      loadUsers();
    } catch (err) {
      flash(err.message, true);
    }
  };

  const handleDelete = async (user) => {
    if (!canManage(user)) { flash('Nur Super-Admin kann andere Admins löschen', true); return; }
    if (!window.confirm(`Nutzer "${user.email}" wirklich löschen?\n\nDas Konto wird komplett entfernt. Wenn du nur die aktive Session beenden willst, nutze stattdessen "Kicken".`)) return;
    try {
      const res = await authFetch(`/api/aether/auth/users/${user.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      flash(`"${user.email}" gelöscht`);
      loadUsers();
    } catch (err) {
      flash(err.message, true);
    }
  };

  const handleKick = async (user) => {
    if (!canManage(user)) { flash('Nur Super-Admin kann andere Admins kicken', true); return; }
    if (!window.confirm(`"${user.email}" jetzt aus allen Sessions kicken?\n\nDas Konto bleibt aktiv — der Nutzer muss sich nur neu einloggen.`)) return;
    try {
      const res = await authFetch(`/api/aether/auth/users/${user.id}/kick`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      flash(`"${user.email}" wurde gekickt`);
      loadUsers();
    } catch (err) {
      flash(err.message, true);
    }
  };

  const handleSendLoginInfo = async (user) => {
    try {
      const res = await authFetch(`/api/aether/auth/users/${user.id}/send-login-info`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      flash(data.delivered ? `Login-Info an "${user.email}" gesendet` : `Konnte E-Mail nicht zustellen (Logs prüfen)`, !data.delivered);
    } catch (err) {
      flash(err.message, true);
    }
  };

  const handleSendPasswordReset = async (user) => {
    if (!window.confirm(`Reset-Link an "${user.email}" senden?\n\nDer Link ist 30 Minuten gültig.`)) return;
    try {
      const res = await authFetch(`/api/aether/auth/users/${user.id}/send-password-reset`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      flash(data.delivered ? `Reset-Link an "${user.email}" gesendet` : `Reset-Link erzeugt, aber Mail nicht zugestellt`, !data.delivered);
    } catch (err) {
      flash(err.message, true);
    }
  };

  // ===== Edit modal =====
  const openEdit = (user) => {
    if (!canManage(user)) { flash('Nur Super-Admin kann andere Admins bearbeiten', true); return; }
    setEditing(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      role: user.role || 'partner',
      is_active: !!user.is_active,
      password: '',
      commission_rate: user.commission_rate != null ? Math.round(user.commission_rate * 100) : 20,
      grant_products: ['instant-curve', 'media-flow', 'instant-grade'],
    });
  };
  const saveEdit = async () => {
    if (!editing) return;
    const payload = {};
    if (editForm.name && editForm.name !== editing.name) payload.name = editForm.name;
    if (editForm.email && editForm.email.toLowerCase() !== (editing.email || '').toLowerCase()) payload.email = editForm.email.toLowerCase();
    if (editForm.role && editForm.role !== editing.role) payload.role = editForm.role;
    if (editForm.is_active !== editing.is_active) payload.is_active = !!editForm.is_active;
    if (editForm.password) {
      if (editForm.password.length < 8) { flash('Passwort min. 8 Zeichen', true); return; }
      payload.password = editForm.password;
    }
    // Provision — send when partner/admin and the rate actually changed.
    const roleNow = editForm.role || editing.role;
    if (roleNow === 'partner' || roleNow === 'admin') {
      const newRate = Math.round(Math.max(0, Math.min(100, Number(editForm.commission_rate) || 0))) / 100;
      if (editing.commission_rate == null || Math.abs(newRate - Number(editing.commission_rate)) > 1e-6) {
        payload.commission_rate = newRate;
      }
    }
    if (Object.keys(payload).length === 0) { setEditing(null); return; }
    setSavingEdit(true);
    try {
      const res = await authFetch(`/api/aether/auth/users/${editing.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      // Promote (non-partner -> partner): unlock the selected products too.
      let grantNote = '';
      if (editForm.role === 'partner' && editing.role !== 'partner' && (editForm.grant_products || []).length) {
        const failed = await grantPartnerProducts((data.email || editing.email).toLowerCase(), editForm.grant_products);
        grantNote = failed.length ? ` (Freischaltung fehlgeschlagen: ${failed.join(', ')})` : ' + Produkte freigeschaltet';
      }
      flash(`"${editing.email}" aktualisiert${grantNote}`);
      setEditing(null);
      loadUsers();
    } catch (err) {
      flash(err.message, true);
    } finally {
      setSavingEdit(false);
    }
  };

  // Role filter: 'all' | 'admin' | 'partner' | 'customer'
  const [roleFilter, setRoleFilter] = useState('all');

  const filtered = users.filter(u => {
    if (roleFilter !== 'all') {
      // Treat any legacy/empty role as customer for filter purposes.
      const r = u.role || 'customer';
      if (r !== roleFilter) return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    return u.email.toLowerCase().includes(s) || (u.name || '').toLowerCase().includes(s);
  });
  const adminCount    = users.filter(u => u.role === 'admin').length;
  const partnerCount  = users.filter(u => u.role === 'partner').length;
  const customerCount = users.filter(u => (u.role || 'customer') === 'customer').length;
  const activeCount   = users.filter(u => u.is_active).length;

  // If a user is selected for detail, render the detail page instead.
  if (detailUserId) {
    return (
      <UserDetail
        loading={detailLoading}
        data={detailData}
        onBack={closeDetail}
        onEdit={(u) => { closeDetail(); openEdit(u); }}
      />
    );
  }

  return (
    <div className="w-full" data-testid="aether-user-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-medium tracking-tight uppercase">Nutzerverwaltung</h2>
          <p className="font-mono text-[11px] text-white/40 uppercase tracking-widest mt-1">
            Aether Zugang &amp; Rollen verwalten
          </p>
          {!meIsSuperAdmin && (
            <p className="font-mono text-[10px] text-amber-300/60 mt-2">
              Du bist Admin, aber nicht Super-Admin — du kannst keine anderen Admins bearbeiten/löschen.
            </p>
          )}
        </div>
        {(
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-5 py-3 bg-white text-black rounded-xl font-mono text-[11px] uppercase tracking-widest hover:bg-white/90 transition"
            data-testid="aether-user-create-btn"
          >
            <UserPlus size={14} /> Neuer Nutzer
          </button>
        )}
      </div>

      {/* Flash messages */}
      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 font-mono text-[11px]" data-testid="aether-user-error">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400 font-mono text-[11px]" data-testid="aether-user-success">
          <CheckCircle2 size={14} /> {success}
        </div>
      )}

      {(
        <>
          {/* Stats — Customer added next to Admin / Partner */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
            <StatCard label="Gesamt"   value={users.length}   icon={Users} />
            <StatCard label="Aktiv"    value={activeCount}    icon={CheckCircle2} accent="text-emerald-400" />
            <StatCard label="Admins"   value={adminCount}     icon={Shield}       accent="text-amber-400" />
            <StatCard label="Partner"  value={partnerCount}   icon={UserIcon}     accent="text-sky-400" />
            <StatCard label="Kunden"   value={customerCount}  icon={UserIcon}     accent="text-violet-400" />
          </div>

          {/* Role filter pills */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {[
              { id: 'all',      label: 'Alle',    count: users.length },
              { id: 'admin',    label: 'Admins',  count: adminCount },
              { id: 'partner',  label: 'Partner', count: partnerCount },
              { id: 'customer', label: 'Kunden',  count: customerCount },
            ].map(t => {
              const isActive = roleFilter === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setRoleFilter(t.id)}
                  className={`px-3 py-1.5 rounded-lg flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest transition ${
                    isActive ? 'bg-white text-black' : 'bg-white/[0.04] text-white/60 hover:text-white hover:bg-white/[0.08]'
                  }`}
                  data-testid={`aether-user-role-filter-${t.id}`}
                >
                  <span>{t.label}</span>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${isActive ? 'bg-black/10 text-black' : 'bg-white/10 text-white/70'}`}>{t.count}</span>
                </button>
              );
            })}
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nutzer suchen..."
              className="w-full pl-10 pr-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl font-mono text-[12px] text-white placeholder:text-white/20 focus:outline-none focus:border-white/30 transition"
              data-testid="aether-user-search"
            />
          </div>

          {/* List */}
          {loading ? (
            <div className="flex items-center justify-center py-12 text-white/40">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 font-mono text-[11px] uppercase tracking-widest text-white/30">
              Keine Nutzer gefunden
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((u) => (
                <UserCard
                  key={u.id}
                  user={u}
                  isMe={u.id === me?.id}
                  canManage={canManage(u)}
                  onEdit={openEdit}
                  onToggleActive={handleToggleActive}
                  onDelete={handleDelete}
                  onKick={handleKick}
                  onSendLoginInfo={handleSendLoginInfo}
                  onSendPasswordReset={handleSendPasswordReset}
                  onOpenDetail={openDetail}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="aether-user-create-modal">
          <div className="monolith-heavy w-full max-w-md p-8 relative">
            <button onClick={() => setShowCreate(false)} className="absolute top-4 right-4 p-2 text-white/40 hover:text-white">
              <X size={16} />
            </button>
            <h3 className="text-xl font-medium tracking-tight uppercase mb-1">Neuer Nutzer</h3>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-6">Anlegen &amp; Rolle zuweisen</p>
            <form onSubmit={handleCreate} className="space-y-4">
              <Field label="Name" icon={UserIcon}>
                <input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="modal-input" placeholder="Vollständiger Name" autoFocus />
              </Field>
              <Field label="E-Mail" icon={Mail}>
                <input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="modal-input" placeholder="user@example.com" />
              </Field>
              <Field label="Passwort (min. 8 Zeichen)" icon={KeyRound}>
                <input type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="modal-input" placeholder="••••••••" />
              </Field>
              <Field label="Rolle" icon={Shield}>
                <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="modal-input">
                  <option value="customer">Kunde — minimaler Zugang (eigene Bestellungen, Lizenzen)</option>
                  <option value="partner">Partner — Affiliate mit Provisions-Dashboard</option>
                  <option value="admin">Admin — volle Rechte</option>
                </select>
              </Field>
              {newUser.role === 'partner' && (
                <label className="flex items-start gap-2 cursor-pointer select-none text-xs leading-relaxed text-white/70">
                  <input
                    type="checkbox"
                    checked={newUser.create_affiliate}
                    onChange={(e) => setNewUser({ ...newUser, create_affiliate: e.target.checked })}
                    className="accent-orange-500 mt-0.5"
                  />
                  <span>
                    <span className="text-white">Affiliate-Datensatz gleich anlegen</span><br />
                    Generiert einen Tracking-Code und verknüpft ihn mit diesem Account.
                  </span>
                </label>
              )}
              {(newUser.role === 'partner' || newUser.role === 'admin') && (
                <Field label="Provision (%)" icon={DollarSign}>
                  <input
                    type="number" min="0" max="100" step="1"
                    value={newUser.commission_rate}
                    onChange={(e) => setNewUser({ ...newUser, commission_rate: e.target.value })}
                    className="modal-input" placeholder="20"
                  />
                </Field>
              )}
              {newUser.role === 'partner' && (
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 block">Produkte freischalten</label>
                  <div className="space-y-2">
                    {[['instant-curve', 'Instant Curve'], ['media-flow', 'Media Flow'], ['instant-grade', 'Instant Grade']].map(([pid, pname]) => {
                      const checked = (newUser.grant_products || []).includes(pid);
                      return (
                        <label key={pid} className="flex items-center gap-2 cursor-pointer select-none text-xs text-white/70">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const set = new Set(newUser.grant_products || []);
                              if (e.target.checked) set.add(pid); else set.delete(pid);
                              setNewUser({ ...newUser, grant_products: [...set] });
                            }}
                            className="accent-orange-500"
                          />
                          <span className="text-white">{pname}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="font-mono text-[9px] text-white/30 mt-1.5">Legt echte Lizenz-Keys für den Partner an (wie ein Geschenk-Grant).</p>
                </div>
              )}
              <button type="submit" disabled={creating} className="w-full py-3 bg-white text-black rounded-xl font-mono text-[11px] uppercase tracking-widest hover:bg-white/90 transition disabled:opacity-50 flex items-center justify-center gap-2">
                {creating ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                {creating ? 'Anlegen' : 'Nutzer anlegen'}
              </button>
            </form>
          </div>
          <ModalStyles />
        </div>
      )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="monolith-heavy w-full max-w-md p-8 relative">
            <button onClick={() => setEditing(null)} className="absolute top-4 right-4 p-2 text-white/40 hover:text-white">
              <X size={16} />
            </button>
            <h3 className="text-xl font-medium tracking-tight uppercase mb-1">Profil bearbeiten</h3>
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-6 truncate">{editing.email}</p>
            <div className="space-y-4">
              <Field label="Name" icon={UserIcon}>
                <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="modal-input" autoFocus />
              </Field>
              <Field label="E-Mail" icon={Mail}>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="modal-input"
                  disabled={editing.is_super_admin}
                />
                {editing.is_super_admin && (
                  <p className="font-mono text-[9px] text-amber-300/70 mt-1">Super-Admin-E-Mail ist gesperrt.</p>
                )}
              </Field>
              <Field label="Rolle" icon={Shield}>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  className="modal-input"
                  disabled={editing.is_super_admin}
                >
                  <option value="customer">Kunde</option>
                  <option value="partner">Partner</option>
                  <option value="admin">Admin</option>
                </select>
              </Field>
              {editing.affiliate_code && (
                <div className="text-xs font-mono p-2 rounded-lg" style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)' }}>
                  <span className="text-white/40">Affiliate-Code:</span>{' '}
                  <span className="text-orange-300 font-medium">{editing.affiliate_code}</span>
                </div>
              )}
              {(editForm.role === 'partner' || editForm.role === 'admin') && (
                <Field label="Provision (%)" icon={DollarSign}>
                  <input
                    type="number" min="0" max="100" step="1"
                    value={editForm.commission_rate}
                    onChange={(e) => setEditForm({ ...editForm, commission_rate: e.target.value })}
                    className="modal-input" placeholder="20"
                  />
                </Field>
              )}
              {editForm.role === 'partner' && editing.role !== 'partner' && (
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2 block">Produkte freischalten</label>
                  <div className="space-y-2">
                    {[['instant-curve', 'Instant Curve'], ['media-flow', 'Media Flow'], ['instant-grade', 'Instant Grade']].map(([pid, pname]) => {
                      const checked = (editForm.grant_products || []).includes(pid);
                      return (
                        <label key={pid} className="flex items-center gap-2 cursor-pointer select-none text-xs text-white/70">
                          <input type="checkbox" checked={checked}
                            onChange={(e) => {
                              const set = new Set(editForm.grant_products || []);
                              if (e.target.checked) set.add(pid); else set.delete(pid);
                              setEditForm({ ...editForm, grant_products: [...set] });
                            }}
                            className="accent-orange-500" />
                          <span className="text-white">{pname}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <Field label="Neues Passwort (optional, leer lassen = unverändert)" icon={KeyRound}>
                <input type="text" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} className="modal-input" placeholder="••••••••" />
              </Field>
              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input type="checkbox" checked={!!editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} className="w-4 h-4 accent-white/80" disabled={editing.is_super_admin} />
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">Aktiv</span>
              </label>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setEditing(null)} className="flex-1 py-3 bg-white/5 hover:bg-white/10 rounded-xl font-mono text-[11px] uppercase tracking-widest text-white/60 hover:text-white transition">
                  Abbrechen
                </button>
                <button onClick={saveEdit} disabled={savingEdit} className="flex-1 py-3 bg-white text-black rounded-xl font-mono text-[11px] uppercase tracking-widest hover:bg-white/90 transition disabled:opacity-50 flex items-center justify-center gap-2">
                  {savingEdit ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Speichern
                </button>
              </div>
            </div>
          </div>
          <ModalStyles />
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent = 'text-white' }) {
  return (
    <div className="monolith-glass p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">{label}</span>
        <Icon size={14} className={`${accent}/60`} />
      </div>
      <div className={`text-3xl font-medium tracking-tight ${accent}`}>{value}</div>
    </div>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-2">
        {Icon && <Icon size={11} />}
        {label}
      </label>
      {children}
    </div>
  );
}

function ModalStyles() {
  return (
    <style>{`
      .modal-input { width:100%; background: rgba(255,255,255,0.02); border:1px solid rgba(255,255,255,0.1); border-radius:10px; padding:12px 14px; font-family:'Space Mono',monospace; font-size:12px; color:white; outline:none; transition:border-color 0.2s; }
      .modal-input:focus { border-color: rgba(255,255,255,0.3); }
      .modal-input:disabled { opacity: 0.4; cursor: not-allowed; }
    `}</style>
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
  } catch { return '—'; }
}

function relativeAgo(iso) {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return null;
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'gerade eben';
    if (m < 60) return `vor ${m} min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `vor ${h} h`;
    const d = Math.floor(h / 24);
    if (d < 30) return `vor ${d} Tagen`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `vor ${mo} Monaten`;
    return `vor ${Math.floor(mo / 12)} Jahren`;
  } catch { return null; }
}

function UserCard({ user, isMe, canManage, onEdit, onToggleActive, onDelete, onKick, onSendLoginInfo, onSendPasswordReset, onOpenDetail }) {
  const isSuper = !!user.is_super_admin;
  const role = user.role || 'customer';
  const Icon = isSuper ? Crown : (role === 'admin' ? Shield : UserIcon);
  // Color the role differently per type so a quick scan tells the story.
  const accent = isSuper
    ? 'text-yellow-300'
    : role === 'admin'    ? 'text-amber-400'
    : role === 'partner'  ? 'text-sky-400'
    : 'text-violet-400'; // customer (or fallback)
  const roleLabel = role === 'admin' ? 'Admin' : role === 'partner' ? 'Partner' : 'Kunde';
  const roleChipTone = role === 'admin'
    ? 'bg-amber-400/10 text-amber-300'
    : role === 'partner' ? 'bg-sky-400/10 text-sky-300'
    : 'bg-violet-400/10 text-violet-300';
  const disabled = !canManage;
  // Build per-button tooltip text — accounts for super-admin lock + self-lock
  const tipEdit = disabled ? 'Nur Super-Admin kann andere Admins bearbeiten' : 'Profil bearbeiten: Name, E-Mail, Rolle, Aktiv-Status, Passwort';
  const tipLogin = 'Login-Info per E-Mail an den Nutzer senden — enthält Login-URL (kein Passwort)';
  const tipReset = 'Reset-Link per E-Mail senden — der Nutzer kann sein Passwort selbst neu setzen (Link 30 min gültig)';
  const tipKick  = isMe ? 'Du kannst dich nicht selbst kicken' : isSuper ? 'Super-Admin kann nicht gekickt werden' : disabled ? 'Nur Super-Admin kann andere Admins kicken' : 'Alle Sessions sofort beenden — Konto bleibt aktiv, Nutzer muss sich neu einloggen';
  const tipToggle = isSuper ? 'Super-Admin kann nicht deaktiviert werden' : disabled ? 'Nur Super-Admin kann andere Admins (de)aktivieren' : user.is_active ? 'Konto deaktivieren — kein Login mehr möglich, alle Sessions enden' : 'Konto reaktivieren';
  const tipDelete = isMe ? 'Du kannst dich nicht selbst löschen' : isSuper ? 'Super-Admin kann nicht gelöscht werden' : disabled ? 'Nur Super-Admin kann andere Admins löschen' : 'Konto endgültig löschen — nicht wiederherstellbar';

  return (
    <div className={`monolith-glass p-5 transition ${!user.is_active ? 'opacity-50' : ''} ${isSuper ? 'ring-1 ring-yellow-400/20' : ''}`} data-testid={`aether-user-card-${user.id}`}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="p-2 rounded-lg bg-white/5 shrink-0">
            <Icon size={16} className={accent} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate flex items-center gap-1.5 flex-wrap">
              {user.name || user.email.split('@')[0]}
              {isMe && <span className="text-[9px] uppercase tracking-widest text-white/40">(du)</span>}
              {isSuper && <span className="text-[8px] uppercase tracking-widest text-yellow-300/80 bg-yellow-300/10 px-1.5 py-0.5 rounded">Super</span>}
              {!isSuper && (
                <span className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded ${roleChipTone}`}>{roleLabel}</span>
              )}
              {user.affiliate_code && (
                <span className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-orange-400/10 text-orange-300" title="Verknüpfter Affiliate-Code">
                  {user.affiliate_code}
                </span>
              )}
            </div>
            <div className="font-mono text-[10px] text-white/40 truncate" title={user.email}>{user.email}</div>
            {user.customer_number && (
              <div className="font-mono text-[9px] text-white/30 mt-0.5">Kundennr. {user.customer_number}</div>
            )}
          </div>
        </div>
      </div>

      {/* Meta details (the main "more details" ask) */}
      <div className="space-y-1.5 mb-4 font-mono text-[10px] text-white/40">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5"><Clock size={10} /> Letzter Login</span>
          <span className="text-white/70" title={fmtDate(user.last_login)}>
            {user.last_login ? (relativeAgo(user.last_login) || fmtDate(user.last_login)) : 'nie'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span>Angelegt</span>
          <span className="text-white/60" title={fmtDate(user.created_at)}>{relativeAgo(user.created_at) || fmtDate(user.created_at)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Logins gesamt</span>
          <span className="text-white/60">{user.login_count || 0}</span>
        </div>
        <div className="flex items-center justify-between">
          <span>Status</span>
          <span className={user.is_active ? 'text-emerald-400' : 'text-amber-400'}>{user.is_active ? 'Aktiv' : 'Inaktiv'}</span>
        </div>
      </div>

      {/* Action buttons — two rows */}
      <div className="grid grid-cols-3 gap-1.5 mb-1.5">
        <ActionBtn icon={Pencil}   tip={tipEdit}  onClick={() => onEdit(user)} disabled={disabled} />
        <ActionBtn icon={Send}     tip={tipLogin} onClick={() => onSendLoginInfo(user)} />
        <ActionBtn icon={KeyRound} tip={tipReset} onClick={() => onSendPasswordReset(user)} />
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        <ActionBtn icon={LogOut} tip={tipKick} onClick={() => onKick(user)} disabled={disabled || isMe || isSuper} tone="warn" />
        <ActionBtn
          icon={user.is_active ? '●' : '○'}
          tip={tipToggle}
          onClick={() => onToggleActive(user)}
          disabled={disabled || isSuper}
          tone={user.is_active ? undefined : 'warn'}
        />
        <ActionBtn icon={Trash2} tip={tipDelete} onClick={() => onDelete(user)} disabled={disabled || isMe || isSuper} tone="danger" />
      </div>

      {/* Detail footer — opens the per-user detail page */}
      {onOpenDetail && (
        <button
          onClick={() => onOpenDetail(user)}
          className="mt-3 pt-3 border-t w-full flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-orange-400 transition-colors"
          style={{ borderColor: 'rgba(255,255,255,0.06)' }}
        >
          <span>Details ansehen</span>
          <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
}

function ActionBtn({ icon: Icon, tip, onClick, disabled, tone }) {
  const base = 'px-2 py-2 rounded-lg font-mono text-[9px] uppercase tracking-widest transition flex items-center justify-center gap-1';
  const tones = {
    default: 'bg-white/5 hover:bg-white/10 text-white/60 hover:text-white',
    warn: 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20',
    danger: 'bg-red-500/5 hover:bg-red-500/15 text-red-400/70 hover:text-red-400',
  };
  const t = tones[tone || 'default'];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-tip={tip}
      className={`${base} ${t} ${disabled ? 'opacity-30 cursor-not-allowed' : ''}`}
    >
      {typeof Icon === 'string' ? <span>{Icon}</span> : <Icon size={11} />}
    </button>
  );
}

/* ====================================================================
   UserDetail — full-page detail view for a single Aether user.
   Tabs: Übersicht (summary + recent), Bestellungen (table), Historie
   (activity log). Renders when AetherUserManagement.detailUserId is set.
   ==================================================================== */
function UserDetail({ loading, data, onBack, onEdit }) {
  const { authFetch } = useAetherAuth();
  const [tab, setTab] = useState('overview');
  // Lizenzen und Archiv kommen aus einer zweiten Quelle: die Lizenztabelle liegt
  // in PostgreSQL und kennt den Kunden nicht — der Weg fuehrt ueber seine
  // Bestellungen. Deshalb ein eigener Aufruf statt alles in einem Endpunkt.
  const [akte, setAkte] = useState(null);
  const email = data?.user?.email;
  useEffect(() => {
    if (!email) return;
    let weg = false;
    (async () => {
      try {
        const res = await authFetch(`/api/admin/customer-file?email=${encodeURIComponent(email)}`);
        const d = await res.json();
        if (!weg && res.ok) setAkte(d);
      } catch { /* Akte ist Beiwerk — die Detailseite steht auch ohne */ }
    })();
    return () => { weg = true; };
  }, [email, authFetch]);

  if (loading || !data) {
    return (
      <div className="w-full">
        <button onClick={onBack} className="mb-6 flex items-center gap-2 text-xs text-white/40 hover:text-white">
          <ArrowLeft size={14} /> Zurück
        </button>
        <div className="flex items-center justify-center py-24 text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      </div>
    );
  }

  const { user, orders, activity, stats } = data;
  const isSuper = !!user.is_super_admin;
  const Icon = isSuper ? Crown : (user.role === 'admin' ? Shield : UserIcon);
  const roleColor = isSuper ? 'bg-yellow-300/15 text-yellow-300' : user.role === 'admin' ? 'bg-amber-400/15 text-amber-300' : 'bg-emerald-400/15 text-emerald-300';

  return (
    <div className="w-full" data-testid="aether-user-detail">
      {/* Breadcrumb + back */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className="flex items-center gap-2 text-xs text-white/40 hover:text-white">
          <span>Nutzerverwaltung</span>
          <ChevronRight size={12} />
          <span className="text-white/70">{user.name || user.email}</span>
        </button>
        <button onClick={onBack} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs text-white/60 hover:text-white liquid-glass">
          <ArrowLeft size={13} /> Zurück
        </button>
      </div>

      {/* Header */}
      <div className="monolith-glass p-6 mb-6">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="p-4 rounded-xl bg-white/5 shrink-0">
            <Icon size={28} className={isSuper ? 'text-yellow-300' : user.role === 'admin' ? 'text-amber-400' : 'text-emerald-400'} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mb-1">Nutzer</p>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold text-white truncate">{user.name || user.email.split('@')[0]}</h1>
              <span className={`px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest ${roleColor}`}>
                {isSuper ? 'Super-Admin' : user.role === 'admin' ? 'Admin' : 'Partner'}
              </span>
              {!user.is_active && <span className="px-2 py-0.5 rounded-full text-[9px] font-mono text-amber-400 bg-amber-400/10">Inaktiv</span>}
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[11px] font-mono">
              <div className="flex"><span className="text-white/30 uppercase tracking-widest mr-2 shrink-0">E-Mail</span><span className="text-white/80 truncate">{user.email}</span></div>
              {user.customer_number && (
                <div className="flex"><span className="text-white/30 uppercase tracking-widest mr-2 shrink-0">Kundennr.</span><span className="text-white/80">{user.customer_number}</span></div>
              )}
              <div className="flex"><span className="text-white/30 uppercase tracking-widest mr-2 shrink-0">Konto seit</span><span className="text-white/80">{fmtDate(user.created_at)}</span></div>
              <div className="flex"><span className="text-white/30 uppercase tracking-widest mr-2 shrink-0">Letzter Login</span><span className="text-white/80">{user.last_login ? `${relativeAgo(user.last_login)} (${fmtDate(user.last_login)})` : 'nie'}</span></div>
              <div className="flex"><span className="text-white/30 uppercase tracking-widest mr-2 shrink-0">Logins gesamt</span><span className="text-white/80">{user.login_count || 0}</span></div>
            </div>
          </div>
          {onEdit && (
            <button onClick={() => onEdit(user)} className="px-3 py-2 rounded-lg text-xs flex items-center gap-2 liquid-glass hover:bg-white/10 text-white/60 hover:text-white shrink-0">
              <Pencil size={12} /> Bearbeiten
            </button>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard label="Bestellungen Gesamt" value={stats?.orders_total ?? 0} icon={Package} />
        <StatCard label="Davon Bezahlt" value={stats?.orders_paid ?? 0} icon={CheckCircle2} accent="text-emerald-400" />
        <StatCard label="Umsatz" value={`${(stats?.total_spent ?? 0).toFixed(2)} €`} icon={DollarSign} accent="text-amber-400" />
      </div>

      {/* Tabs */}
      <div className="border-b mb-4 flex gap-1" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        {[
          { id: 'overview',     label: 'Übersicht',    icon: UserIcon },
          { id: 'orders',       label: 'Bestellungen', icon: Package },
          { id: 'licenses',     label: 'Lizenzen',     icon: Key },
          { id: 'history',      label: 'Historie',     icon: Activity },
          { id: 'archive',      label: 'Gelöscht',     icon: Trash2 },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-xs font-medium flex items-center gap-2 border-b-2 -mb-px transition-colors ${
              tab === t.id ? 'border-orange-500 text-white' : 'border-transparent text-white/40 hover:text-white/70'
            }`}
          >
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="monolith-glass p-5">
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Letzte Bestellungen</h3>
            {orders.length === 0 ? (
              <p className="text-white/30 text-sm font-mono">Noch keine Bestellungen.</p>
            ) : (
              <ul className="space-y-2">
                {orders.slice(0, 5).map((o) => (
                  <li key={o.session_id} className="flex items-center justify-between text-xs font-mono">
                    <span className="text-white/80 truncate">{o.product_name || o.product_id}</span>
                    <span className="text-white/50 shrink-0 ml-3">{fmtDate(o.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="monolith-glass p-5">
            <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-3">Letzte Aktivität</h3>
            {activity.length === 0 ? (
              <p className="text-white/30 text-sm font-mono">Noch keine Aktivität geloggt.</p>
            ) : (
              <ul className="space-y-2">
                {activity.slice(0, 5).map((a) => (
                  <li key={a.id} className="flex items-center justify-between text-xs font-mono">
                    <span className="text-white/80 truncate">{a.action}</span>
                    <span className="text-white/50 shrink-0 ml-3">{a.at ? relativeAgo(a.at) : '—'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {tab === 'licenses' && (
        !akte ? (
          <div className="py-12 text-center font-mono text-xs text-white/30">Lade Lizenzen …</div>
        ) : akte.licenses.length === 0 ? (
          <div className="py-12 text-center font-mono text-xs text-white/30">
            <Key size={32} className="mx-auto mb-3 opacity-40" />
            Keine Lizenzen zu diesem Konto.
          </div>
        ) : (
          <div className="space-y-2">
            {akte.licenses.map((l) => (
              <div key={l.key} className="monolith-glass p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-white">{l.product_name || l.products || '—'}</span>
                    {l.status === 'active' && <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-emerald-400/15 text-emerald-300">aktiv</span>}
                    {l.status === 'deactivated' && <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-amber-400/15 text-amber-300">deaktiviert</span>}
                    {l.status === 'geloescht' && <span className="px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest bg-red-400/15 text-red-300">gelöscht</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <code className={`font-mono text-[11px] px-2 py-0.5 rounded select-all ${
                      l.status === 'active' ? 'text-orange-300 bg-orange-300/10' : 'text-white/40 bg-white/5 line-through'}`}>{l.key}</code>
                    {l.order_number && <span className="font-mono text-[10px] text-white/35">#{l.order_number}</span>}
                  </div>
                </div>
                <div className="font-mono text-[10px] text-white/45 text-right shrink-0">
                  {l.status !== 'geloescht' && (
                    <>{l.activations} von {l.max_activations} Rechnern<br /></>
                  )}
                  {l.created_at && fmtDate(l.created_at)}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'archive' && (
        !akte ? (
          <div className="py-12 text-center font-mono text-xs text-white/30">Lade Archiv …</div>
        ) : akte.archive.length === 0 ? (
          <div className="py-12 text-center font-mono text-xs text-white/30">
            <Trash2 size={32} className="mx-auto mb-3 opacity-40" />
            Bei diesem Kunden wurde nichts gelöscht.
          </div>
        ) : (
          <div className="space-y-2">
            {akte.archive.map((a) => (
              <div key={a.archive_id} className="monolith-glass p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-sm text-white">
                    {a.kind === 'license' ? 'Lizenz' : a.kind === 'order' ? 'Bestellung' : a.kind}
                    <span className="text-white/45 font-mono text-[11px] ml-2">{a.original_id}</span>
                  </span>
                  <span className="font-mono text-[10px] text-white/45">
                    {a.archived_at ? fmtDate(a.archived_at) : '—'} · {a.archived_by || 'system'}
                  </span>
                </div>
                <p className="mt-1.5 text-xs text-white/60">
                  {a.reason ? `Grund: ${a.reason}` : 'Kein Grund hinterlegt (vor der Pflichtangabe gelöscht).'}
                </p>
              </div>
            ))}
          </div>
        )
      )}

      {tab === 'orders' && (
        orders.length === 0 ? (
          <div className="py-12 text-center font-mono text-xs text-white/30">
            <Package size={32} className="mx-auto mb-3 opacity-40" />
            Keine Bestellungen für diese E-Mail-Adresse.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-[9px] uppercase tracking-widest text-white/40 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <th className="px-4 py-2 font-medium">Datum</th>
                  <th className="px-4 py-2 font-medium">Produkt</th>
                  <th className="px-4 py-2 font-medium">Order #</th>
                  <th className="px-4 py-2 font-medium">Betrag</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.session_id} className="border-b last:border-0 hover:bg-white/[0.02]" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                    <td className="px-4 py-2.5 text-white/60 font-mono">{fmtDate(o.created_at)}</td>
                    <td className="px-4 py-2.5 text-white">{o.product_name || o.product_id}</td>
                    <td className="px-4 py-2.5 text-white/50 font-mono">{o.order_number || '—'}</td>
                    <td className="px-4 py-2.5 text-white font-mono">{(o.amount ?? 0).toFixed(2)} €</td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded text-[8px] font-mono uppercase ${
                        o.payment_status === 'paid' ? 'bg-green-500/20 text-green-300' :
                        o.payment_status === 'free' ? 'bg-blue-500/20 text-blue-300' :
                        'bg-yellow-500/20 text-yellow-300'
                      }`}>{o.payment_status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {tab === 'history' && (
        activity.length === 0 ? (
          <div className="py-12 text-center font-mono text-xs text-white/30">
            <Activity size={32} className="mx-auto mb-3 opacity-40" />
            Keine Aktivität für diesen Nutzer im Audit-Log.
          </div>
        ) : (
          <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <ul>
              {activity.map((a) => (
                <li key={a.id} className="px-4 py-3 border-b last:border-0 flex flex-wrap items-center gap-3" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-mono bg-white/10 text-white/80 shrink-0">{a.action}</span>
                  <span className="text-white/60 text-xs font-mono">{a.target_type || ''}{a.target_id ? ` · ${String(a.target_id).slice(0, 24)}` : ''}</span>
                  <span className="ml-auto text-white/40 text-[10px] font-mono">{a.at ? `${relativeAgo(a.at)} · ${fmtDate(a.at)}` : '—'}</span>
                </li>
              ))}
            </ul>
          </div>
        )
      )}
    </div>
  );
}
